// The room shell and its lighting.
//
// Holds the ROOM box, the hard LIGHT_BUDGET, the barrel-vault maths, the
// stepped ceiling sections, the doorway, the brick wall, the alcove boxes cut
// out of ALCOVES, five memoised procedural canvas textures, the two baked light
// maps, buildRoom() (floor, walls, vault, soffits, alcove interiors, section
// signs) and buildLights() (the whole fitting and lamp rig).
//
// This module talks to mergeGeometries directly; it does not use the store.js
// bucket helpers.
//
// Recovered from the production bundle.

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { ROOM_HALF_W, COUNTER_POS, ALCOVES } from './zones.js';
import { floorColorMap, floorRoughnessMap, woodTexture, plasterTexture } from './textures.js';

// ---------------------------------------------------------------------------
// Room geometry constants
// ---------------------------------------------------------------------------

const ROOM = {
    w: 7.8,
    d: 16.5,
    h: 3.55
};

const LIGHT_BUDGET = 13;

const HALF_W = ROOM_HALF_W;

// Floor-runner centre lines.
const RUNNER_X = [-1.45, 0, 1.45];

// Half-width of the barrel vault, and the height the flat soffits sit at.
const VAULT_HALF_W = 2.15;
const SOFFIT_Y = 2.76;

// [z0, z1, ceilingHeight] — the ceiling steps down towards the back of the shop.
const CEILING_SECTIONS = [
    [-2.2, 8.25, 3.05],
    [-5.4, -2.2, 2.8],
    [-8.25, -5.4, 2.55]
];

// The z positions where one ceiling section meets the next.
const CEILING_STEP_Z = [-2.2, -5.4];

const SIGN_Y = 2.15;

const DOOR = {
    x: 1.1,
    w: 1.05,
    h: 2.05
};

const BRICK_WALL = {
    z0: -2.2,
    z1: 3.8,
    y0: 1.1
};

const ALCOVE_BOXES = ALCOVES.map(zone => ({
    id: zone.id,
    entrance: !!zone.entrance,
    side: zone.alcove.side,
    z: zone.alcove.z,
    w: zone.alcove.w,
    d: zone.alcove.d,
    openingW: zone.alcove.openingW,
    h: zone.alcove.ceil
}));

const alcoveCenterX = box => box.side * (HALF_W + box.w / 2);

// Depth of the alcove door jamb.
const JAMB_D = 0.35;

// ---------------------------------------------------------------------------
// Local canvas helper and the memoised procedural textures
// ---------------------------------------------------------------------------

function makeCanvas(width, height) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    return [canvas, canvas.getContext("2d")];
}

let _ceilingBoardTex;

function ceilingBoardTexture() {
    if (_ceilingBoardTex) return _ceilingBoardTex;
    const [canvas, ctx] = makeCanvas(256, 256);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, 256, 256);
    for (let x = 0; x < 256; x += 32) {
        ctx.fillStyle = "rgba(150,144,132,.34)";
        ctx.fillRect(x, 0, 1.5, 256);
        ctx.fillStyle = "rgba(255,255,255,.9)";
        ctx.fillRect(x + 2, 0, 1.5, 256);
    }
    for (let i = 0; i < 900; i++) {
        ctx.fillStyle = "rgba(214,208,196,.16)";
        ctx.fillRect(Math.random() * 256, Math.random() * 256, 2, 2);
    }
    _ceilingBoardTex = new THREE.CanvasTexture(canvas);
    _ceilingBoardTex.wrapS = _ceilingBoardTex.wrapT = THREE.RepeatWrapping;
    _ceilingBoardTex.repeat.set(VAULT_HALF_W * 2 / 0.88, ROOM.d / 3);
    _ceilingBoardTex.colorSpace = THREE.SRGBColorSpace;
    return _ceilingBoardTex;
}

let _brickTex;

function brickTexture() {
    if (_brickTex) return _brickTex;
    const [canvas, ctx] = makeCanvas(256, 256);
    ctx.fillStyle = "#6f4234";
    ctx.fillRect(0, 0, 256, 256);
    const courseH = 32;
    const brickW = 64;
    for (let course = 0, y = 0; y < 256; y += courseH, course++) {
        const stagger = course % 2 * (brickW / 2);
        for (let x = -brickW; x < 256 + brickW; x += brickW) {
            const shade = 0.75 + Math.random() * 0.45;
            ctx.fillStyle = `rgb(${111*shade|0},${66*shade|0},${52*shade|0})`;
            ctx.fillRect(x + stagger + 2, y + 2, brickW - 4, courseH - 4);
        }
        ctx.fillStyle = "rgba(28,24,20,.85)";
        ctx.fillRect(0, y, 256, 2);
    }
    _brickTex = new THREE.CanvasTexture(canvas);
    _brickTex.wrapS = _brickTex.wrapT = THREE.RepeatWrapping;
    _brickTex.repeat.set(8, 2);
    _brickTex.colorSpace = THREE.SRGBColorSpace;
    return _brickTex;
}

let _bulbTex;

function bulbGradientTexture() {
    if (_bulbTex) return _bulbTex;
    const [canvas, ctx] = makeCanvas(8, 64);
    const gradient = ctx.createLinearGradient(0, 0, 0, 64);
    gradient.addColorStop(0, "#ffffff");
    gradient.addColorStop(0.45, "#e2d8c6");
    gradient.addColorStop(0.8, "#a2947c");
    gradient.addColorStop(1, "#6e6252");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 8, 64);
    _bulbTex = new THREE.CanvasTexture(canvas);
    _bulbTex.colorSpace = THREE.SRGBColorSpace;
    return _bulbTex;
}

let _rugTex;

function rugTexture() {
    if (_rugTex) return _rugTex;
    const [canvas, ctx] = makeCanvas(128, 256);
    ctx.fillStyle = "#241c14";
    ctx.fillRect(0, 0, 128, 256);
    ctx.strokeStyle = "#5a4028";
    ctx.lineWidth = 8;
    ctx.strokeRect(8, 8, 112, 240);
    ctx.strokeStyle = "rgba(120,92,58,.45)";
    ctx.lineWidth = 2;
    for (let y = 40; y < 256; y += 48) {
        ctx.beginPath();
        ctx.ellipse(64, y, 22, 16, 0, 0, Math.PI * 2);
        ctx.stroke();
    }
    _rugTex = new THREE.CanvasTexture(canvas);
    _rugTex.colorSpace = THREE.SRGBColorSpace;
    return _rugTex;
}

let _carpetTex;

function carpetTexture() {
    if (_carpetTex) return _carpetTex;
    const [canvas, ctx] = makeCanvas(128, 128);
    ctx.fillStyle = "#4a2028";
    ctx.fillRect(0, 0, 128, 128);
    for (let i = 0; i < 3e3; i++) {
        const fleck = Math.random() < 0.5 ? "rgba(94,38,48,.5)" : "rgba(40,14,20,.5)";
        ctx.fillStyle = fleck;
        ctx.fillRect(Math.random() * 128, Math.random() * 128, 2, 2);
    }
    _carpetTex = new THREE.CanvasTexture(canvas);
    _carpetTex.wrapS = _carpetTex.wrapT = THREE.RepeatWrapping;
    _carpetTex.repeat.set(4, 4);
    _carpetTex.colorSpace = THREE.SRGBColorSpace;
    return _carpetTex;
}

// ---------------------------------------------------------------------------
// Fitting layout constants used by both the baked light maps and buildLights()
// ---------------------------------------------------------------------------

const WIDE_SCALE = ROOM.w / 5.8;
const PENDANT_X = -0.95 * WIDE_SCALE;
const PENDANT_Z = [7.4, 5.4, 3.3, 1.4, -0.6, -2.9, -4.7, -6.9];
const PANEL_Z = [-0.2, 2.9, 6.2];
const BATTEN_Z = [-7.4, -4.6, -1.2, 2.4, 5.6];

// Soft radial splat used by every baked glow map in this module.
function glowBlob(ctx, x, y, radius, alpha, rgb = "255,255,255") {
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
    gradient.addColorStop(0, `rgba(${rgb},${alpha})`);
    gradient.addColorStop(0.45, `rgba(${rgb},${alpha*0.42})`);
    gradient.addColorStop(1, `rgba(${rgb},0)`);
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
}

// Baked emissive map for one vault section, spanning z0..z1. 48 px per metre.
function vaultEmissiveMap(z0, z1) {
    const width = Math.round(VAULT_HALF_W * 2 * 48);
    const height = Math.max(16, Math.round((z1 - z0) * 48));
    const [canvas, ctx] = makeCanvas(width, height);
    ctx.fillStyle = "#a3a19f";
    ctx.fillRect(0, 0, width, height);
    ctx.globalCompositeOperation = "lighter";
    const toU = x => (x + VAULT_HALF_W) / (VAULT_HALF_W * 2) * width;
    const toV = z => (z1 - z) / (z1 - z0) * height;
    for (const z of PENDANT_Z) {
        if (z < z0 - 2 || z > z1 + 2) continue;
        glowBlob(ctx, toU(PENDANT_X), toV(z), 1.6 * 48, 0.34, "255,216,168);".replace(");", ""));
        glowBlob(ctx, toU(1.35), toV(z), 1.35 * 48, 0.16, "255,234,210");
    }
    for (const z of PANEL_Z) {
        if (z < z0 - 2 || z > z1 + 2) continue;
        glowBlob(ctx, toU(0.55), toV(z), 2.15 * 48, 0.6, "255,251,243");
        glowBlob(ctx, toU(0.55), toV(z), 0.85 * 48, 0.16, "255,252,246");
    }
    for (const z of BATTEN_Z) {
        if (z < z0 - 1.5 || z > z1 + 1.5) continue;
        glowBlob(ctx, toU(1.42 * WIDE_SCALE), toV(z), 1.15 * 48, 0.2, "255,242,222");
        glowBlob(ctx, toU(-2.05 * WIDE_SCALE), toV(z), 1.15 * 48, 0.2, "255,242,222");
    }
    ctx.globalCompositeOperation = "multiply";
    const acrossVault = ctx.createLinearGradient(0, 0, width, 0);
    acrossVault.addColorStop(0, "#6e6a66");
    acrossVault.addColorStop(0.18, "#c8c4c0");
    acrossVault.addColorStop(0.5, "#ffffff");
    acrossVault.addColorStop(0.82, "#cbc7c2");
    acrossVault.addColorStop(1, "#726d68");
    ctx.fillStyle = acrossVault;
    ctx.fillRect(0, 0, width, height);
    const alongVault = ctx.createLinearGradient(0, 0, 0, height);
    alongVault.addColorStop(0, "#dedad4");
    alongVault.addColorStop(0.42, "#ffffff");
    alongVault.addColorStop(1, "#e8e4de");
    ctx.fillStyle = alongVault;
    ctx.fillRect(0, 0, width, height);
    for (let x = 0; x < width; x += 0.11 * 48) {
        ctx.fillStyle = "rgba(150,146,138,0.30)";
        ctx.fillRect(Math.round(x), 0, 1, height);
        ctx.fillStyle = "rgba(255,255,255,0.22)";
        ctx.fillRect(Math.round(x) + 1, 0, 1, height);
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
}

// ---------------------------------------------------------------------------
// Barrel-vault maths
// ---------------------------------------------------------------------------

// Rise of the vault at its crown, and the radius of the circle that produces it
// across a half-width of VAULT_HALF_W.
const VAULT_RISE = 0.28;
const VAULT_R = (VAULT_HALF_W * VAULT_HALF_W + VAULT_RISE * VAULT_RISE) / (2 * VAULT_RISE);

function vaultY(x, ceilingY) {
    const clampedX = Math.min(Math.abs(x), VAULT_HALF_W);
    return ceilingY - VAULT_R + Math.sqrt(VAULT_R * VAULT_R - clampedX * clampedX);
}

function buildVaultGeometry(ceilingY, z0, z1, arcSegments = 32) {
    const lengthSegments = Math.max(2, Math.round((z1 - z0) * 2));
    const positions = [];
    const normals = [];
    const uvs = [];
    const indices = [];
    for (let i = 0; i <= arcSegments; i++) {
        const u = i / arcSegments;
        const x = -VAULT_HALF_W + u * VAULT_HALF_W * 2;
        const y = vaultY(x, ceilingY);
        const normalY = -(y - (ceilingY - VAULT_R)) / VAULT_R;
        const normalX = -x / VAULT_R;
        for (let j = 0; j <= lengthSegments; j++) {
            const v = j / lengthSegments;
            positions.push(x, y, z0 + v * (z1 - z0));
            normals.push(normalX, normalY, 0);
            uvs.push(u, v);
        }
    }
    const stride = lengthSegments + 1;
    for (let i = 0; i < arcSegments; i++)
        for (let j = 0; j < lengthSegments; j++) {
            const a = i * stride + j;
            const b = a + stride;
            indices.push(a, b, a + 1, b, b + 1, a + 1);
        }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
    geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);
    return geometry;
}

// Baked floor pool-of-light map. 12 px per metre.
function floorGlowMap() {
    const width = Math.round(ROOM.w * 12);
    const height = Math.round(ROOM.d * 12);
    const [canvas, ctx] = makeCanvas(width, height);
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, width, height);
    ctx.globalCompositeOperation = "lighter";
    const toU = x => (x + ROOM.w / 2) / ROOM.w * width;
    const toV = z => (ROOM.d / 2 - z) / ROOM.d * height;
    for (const z of PENDANT_Z) glowBlob(ctx, toU(PENDANT_X), toV(z), 1.5 * 12, 0.85);
    for (const z of PANEL_Z) glowBlob(ctx, toU(0.55), toV(z), 1.8 * 12, 0.7);
    glowBlob(ctx, toU(-2.6), toV(0.4), 1.1 * 12, 0.45);
    glowBlob(ctx, toU(2.6), toV(-3.4), 1 * 12, 0.4);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
}

function ceilingHeightAt(z) {
    for (const [z0, z1, height] of CEILING_SECTIONS)
        if (z >= z0 && z < z1) return height;
    return ROOM.h;
}

// ---------------------------------------------------------------------------
// buildRoom
// ---------------------------------------------------------------------------

function buildRoom(parent) {
    const group = new THREE.Group();

    const plankWidth = 0.235;
    const floorMap = floorColorMap().clone();
    floorMap.needsUpdate = true;
    floorMap.center.set(0.5, 0.5);
    floorMap.rotation = Math.PI / 2;
    floorMap.repeat.set(ROOM.w / plankWidth / 5, ROOM.d / 3.2);
    const floorRough = floorRoughnessMap().clone();
    floorRough.needsUpdate = true;
    floorRough.center.set(0.5, 0.5);
    floorRough.rotation = Math.PI / 2;
    floorRough.repeat.copy(floorMap.repeat);
    const floorMaterial = new THREE.MeshStandardMaterial({
        map: floorMap,
        roughnessMap: floorRough,
        bumpMap: floorMap,
        bumpScale: 0.6,
        roughness: 0.9,
        metalness: 0,
        color: 0x5e3c1e
    });
    {
        const floor = new THREE.Mesh(new THREE.PlaneGeometry(ROOM.w, ROOM.d), floorMaterial);
        floor.rotation.x = -Math.PI / 2;
        floor.position.set(0, 0, 0);
        floor.receiveShadow = true;
        group.add(floor);
    }

    const runnerMaterial = new THREE.MeshBasicMaterial({
        color: 2828836,
        transparent: true,
        opacity: 0.22,
        depthWrite: false
    });
    for (const x of RUNNER_X) {
        const runner = new THREE.Mesh(new THREE.PlaneGeometry(0.88, ROOM.d - 3), runnerMaterial);
        runner.rotation.x = -Math.PI / 2;
        runner.position.set(x, 0.002, -0.4);
        group.add(runner);
    }

    {
        // Built, hidden and never added to the group.
        const floorGlow = new THREE.Mesh(new THREE.PlaneGeometry(ROOM.w, ROOM.d), new THREE.MeshBasicMaterial({
            map: floorGlowMap(),
            blending: THREE.AdditiveBlending,
            depthWrite: false,
            transparent: true,
            fog: true,
            color: new THREE.Color().setRGB(0.042, 0.03, 0.017)
        }));
        floorGlow.rotation.x = -Math.PI / 2;
        floorGlow.position.set(0, 0.004, 0);
        floorGlow.visible = false;
    }

    const rugMaterial = new THREE.MeshStandardMaterial({
        map: rugTexture(),
        roughness: 0.99,
        metalness: 0,
        color: 4865070
    });
    for (const [x, z, w, d] of [
            [1.45, 6.6, 0.85, 1.8]
        ]) {
        const rug = new THREE.Mesh(new THREE.PlaneGeometry(w, d), rugMaterial);
        rug.rotation.x = -Math.PI / 2;
        rug.position.set(x, 0.004, z);
        rug.receiveShadow = true;
        group.add(rug);
    }

    const plasterMaterial = new THREE.MeshStandardMaterial({
        map: plasterTexture(),
        bumpMap: plasterTexture(),
        bumpScale: 0.5,
        roughness: 0.94,
        metalness: 0,
        color: 12102550
    });
    const wallPanel = (width, height, position, yaw, material = plasterMaterial) => {
        const mesh = new THREE.Mesh(new THREE.PlaneGeometry(width, height), material);
        mesh.position.set(...position);
        mesh.rotation.y = yaw;
        mesh.receiveShadow = true;
        group.add(mesh);
        return mesh;
    };

    // Back wall.
    wallPanel(ROOM.w, ROOM.h, [0, ROOM.h / 2, -ROOM.d / 2], 0);

    {
        // Side walls, painted in z bands, with the alcove openings cut out.
        const bands = [{
            z0: 4.6,
            z1: ROOM.d / 2,
            color: 3813672,
            rough: 0.97
        }, {
            z0: 1.2,
            z1: 4.6,
            color: 3025446,
            rough: 0.95
        }, {
            z0: -2.2,
            z1: 1.2,
            color: 3810368,
            rough: 0.55
        }, {
            z0: -5.4,
            z1: -2.2,
            color: 1974566,
            rough: 0.9
        }, {
            z0: -ROOM.d / 2,
            z1: -5.4,
            color: 2304563,
            rough: 0.92
        }];
        const bandMaterials = new Map();
        for (const band of bands) bandMaterials.set(band, new THREE.MeshStandardMaterial({
            map: plasterTexture(),
            roughness: band.rough,
            metalness: 0,
            color: band.color
        }));
        for (const side of [-1, 1]) {
            const wallX = side * HALF_W;
            const openings = ALCOVE_BOXES.filter(box => box.side === side).map(box => [box.z - box.openingW / 2, box.z + box.openingW / 2]);
            for (const band of bands) {
                let spans = [
                    [band.z0, band.z1]
                ];
                for (const [openStart, openEnd] of openings) {
                    const kept = [];
                    for (const [spanStart, spanEnd] of spans) {
                        if (openEnd <= spanStart || openStart >= spanEnd) {
                            kept.push([spanStart, spanEnd]);
                            continue;
                        }
                        if (openStart > spanStart) kept.push([spanStart, openStart]);
                        if (openEnd < spanEnd) kept.push([openEnd, spanEnd]);
                    }
                    spans = kept;
                }
                for (const [spanStart, spanEnd] of spans) {
                    if (spanEnd - spanStart < 0.02) continue;
                    wallPanel(spanEnd - spanStart, ROOM.h, [wallX, ROOM.h / 2, (spanStart + spanEnd) / 2], side < 0 ? Math.PI / 2 : -Math.PI / 2, bandMaterials.get(band));
                }
            }
            // Header above each alcove opening.
            for (const box of ALCOVE_BOXES.filter(candidate => candidate.side === side)) {
                const headerHeight = ROOM.h - box.h;
                wallPanel(box.openingW, headerHeight, [wallX, box.h + headerHeight / 2, box.z], side < 0 ? Math.PI / 2 : -Math.PI / 2);
            }
        }
    }

    const brickMaterial = new THREE.MeshStandardMaterial({
        map: brickTexture(),
        bumpMap: brickTexture(),
        bumpScale: 1.5,
        roughness: 0.98,
        metalness: 0,
        color: 3351324
    });
    {
        const brickX = -HALF_W + 0.02;
        const brickSpan = BRICK_WALL.z1 - BRICK_WALL.z0;
        const gaps = ALCOVE_BOXES.filter(box => box.side === -1).map(box => [box.z - box.openingW / 2 - 0.04, box.z + box.openingW / 2 + 0.04]).sort((a, b) => a[0] - b[0]);
        const spans = [];
        let cursor = BRICK_WALL.z0;
        for (const [gapStart, gapEnd] of gaps) {
            if (gapEnd <= BRICK_WALL.z0 || gapStart >= BRICK_WALL.z1) continue;
            if (gapStart > cursor) spans.push([cursor, Math.min(gapStart, BRICK_WALL.z1)]);
            cursor = Math.max(cursor, Math.min(gapEnd, BRICK_WALL.z1));
        }
        if (cursor < BRICK_WALL.z1) spans.push([cursor, BRICK_WALL.z1]);
        for (const [spanStart, spanEnd] of spans) {
            const spanWidth = spanEnd - spanStart;
            if (spanWidth < 0.05) continue;
            // Re-cut u so each span samples its own slice of the brick run.
            const geometry = new THREE.PlaneGeometry(spanWidth, ROOM.h - BRICK_WALL.y0);
            const uv = geometry.attributes.uv;
            const uOffset = (BRICK_WALL.z1 - spanEnd) / brickSpan;
            const uScale = spanWidth / brickSpan;
            for (let i = 0; i < uv.count; i++) uv.setX(i, uOffset + uv.getX(i) * uScale);
            uv.needsUpdate = true;
            const mesh = new THREE.Mesh(geometry, brickMaterial);
            mesh.rotation.y = Math.PI / 2;
            mesh.position.set(brickX, (BRICK_WALL.y0 + ROOM.h) / 2, (spanStart + spanEnd) / 2);
            mesh.receiveShadow = true;
            group.add(mesh);
        }
        const pillar = new THREE.Mesh(new THREE.BoxGeometry(0.36, ROOM.h, 0.36), new THREE.MeshStandardMaterial({
            color: 14077370,
            roughness: 0.9
        }));
        pillar.position.set(-HALF_W + 0.18, ROOM.h / 2, BRICK_WALL.z1 + 0.18);
        pillar.castShadow = true;
        pillar.receiveShadow = true;
        group.add(pillar);
        const rightBrickStrip = new THREE.Mesh(new THREE.PlaneGeometry(6, 0.7), brickMaterial);
        rightBrickStrip.rotation.y = -Math.PI / 2;
        rightBrickStrip.position.set(HALF_W - 0.02, 2.45, 0.6);
        rightBrickStrip.receiveShadow = true;
        group.add(rightBrickStrip);
    }

    {
        // Front wall and the doorway.
        const frontZ = ROOM.d / 2;
        const doorLeft = DOOR.x - DOOR.w / 2;
        const doorRight = DOOR.x + DOOR.w / 2;
        const leftWidth = doorLeft + HALF_W;
        wallPanel(leftWidth, ROOM.h, [-HALF_W + leftWidth / 2, ROOM.h / 2, frontZ], Math.PI);
        const rightWidth = HALF_W - doorRight;
        wallPanel(rightWidth, ROOM.h, [doorRight + rightWidth / 2, ROOM.h / 2, frontZ], Math.PI);
        const headerHeight = ROOM.h - DOOR.h;
        wallPanel(DOOR.w, headerHeight, [DOOR.x, DOOR.h + headerHeight / 2, frontZ], Math.PI);

        const frameMaterial = new THREE.MeshStandardMaterial({
            color: 14208440,
            roughness: 0.8
        });
        for (const side of [-1, 1]) {
            const jamb = new THREE.Mesh(new THREE.BoxGeometry(0.07, DOOR.h + 0.07, 0.09), frameMaterial);
            jamb.position.set(DOOR.x + side * (DOOR.w / 2 + 0.035), (DOOR.h + 0.07) / 2, frontZ - 0.045);
            jamb.castShadow = true;
            group.add(jamb);
        }
        const lintel = new THREE.Mesh(new THREE.BoxGeometry(DOOR.w + 0.14, 0.07, 0.09), frameMaterial);
        lintel.position.set(DOOR.x, DOOR.h + 0.035, frontZ - 0.045);
        lintel.castShadow = true;
        group.add(lintel);

        // Unlit plane beyond the front wall, facing back through the doorway.
        const outside = new THREE.Mesh(new THREE.PlaneGeometry(DOOR.w + 0.5, DOOR.h + 0.4), new THREE.MeshBasicMaterial({
            color: 1779766,
            fog: false
        }));
        outside.position.set(DOOR.x, (DOOR.h + 0.4) / 2, frontZ + 0.4);
        outside.rotation.y = Math.PI;
        group.add(outside);

        {
            // Additively blended glow plane on the floor inside the door.
            const [canvas, ctx] = makeCanvas(48, 64);
            ctx.fillStyle = "#000";
            ctx.fillRect(0, 0, 48, 64);
            ctx.globalCompositeOperation = "lighter";
            glowBlob(ctx, 24, 8, 40, 0.95, "210,225,255");
            const texture = new THREE.CanvasTexture(canvas);
            texture.colorSpace = THREE.SRGBColorSpace;
            const spill = new THREE.Mesh(new THREE.PlaneGeometry(DOOR.w + 0.5, 1.7), new THREE.MeshBasicMaterial({
                map: texture,
                blending: THREE.AdditiveBlending,
                transparent: true,
                depthWrite: false,
                fog: true,
                color: new THREE.Color().setRGB(0.13, 0.17, 0.26)
            }));
            spill.rotation.x = -Math.PI / 2;
            spill.position.set(DOOR.x, 0.012, frontZ - 0.9);
            group.add(spill);
        }
    }

    // The barrel vault, one shell per ceiling section.
    const boardTexture = ceilingBoardTexture();
    for (const [z0, z1, ceilingY] of CEILING_SECTIONS) {
        const emissiveTrim = ceilingY > 2.9 || ceilingY > 2.7 ? 1 : 1.02;
        const map = boardTexture.clone();
        map.needsUpdate = true;
        map.wrapS = map.wrapT = THREE.RepeatWrapping;
        const arcLength = 2 * VAULT_R * Math.asin(VAULT_HALF_W / VAULT_R);
        map.repeat.set(arcLength / 0.88, (z1 - z0) / 0.88);
        const vault = new THREE.Mesh(buildVaultGeometry(ceilingY, z0, z1), new THREE.MeshStandardMaterial({
            map,
            roughness: 0.92,
            metalness: 0,
            side: THREE.DoubleSide,
            color: 2893859,
            emissive: new THREE.Color(6971218),
            emissiveIntensity: 0.2 * emissiveTrim,
            emissiveMap: vaultEmissiveMap(z0, z1)
        }));
        group.add(vault);
    }

    {
        // Risers where one ceiling section steps down to the next, plus the
        // wooden bulkhead beams that face them.
        const riserMaterial = new THREE.MeshStandardMaterial({
            color: 14275784,
            roughness: 0.95,
            metalness: 0,
            side: THREE.DoubleSide
        });
        const riserGeometries = [];
        for (let i = 0; i < CEILING_STEP_Z.length; i++) {
            const upperY = CEILING_SECTIONS[i][2];
            const lowerY = CEILING_SECTIONS[i + 1][2];
            const riser = new THREE.PlaneGeometry(VAULT_HALF_W * 2, upperY - lowerY);
            riser.translate(0, (upperY + lowerY) / 2, CEILING_STEP_Z[i]);
            riserGeometries.push(riser);
        }
        group.add(new THREE.Mesh(mergeGeometries(riserGeometries, false), riserMaterial));

        const bulkheadGeometries = [];
        for (const z of [...CEILING_STEP_Z, 4.6, 1.2]) {
            const ceilingY = ceilingHeightAt(z + 0.01);
            const beam = new THREE.BoxGeometry(ROOM.w, 0.22, 0.16);
            beam.translate(0, ceilingY - 0.11, z);
            bulkheadGeometries.push(beam);
        }
        const bulkheads = new THREE.Mesh(mergeGeometries(bulkheadGeometries, false), new THREE.MeshStandardMaterial({
            map: woodTexture(-30),
            roughness: 0.95,
            color: 8020292
        }));
        bulkheads.castShadow = false;
        group.add(bulkheads);
    }

    // The flat soffits either side of the vault, and the upstand between the
    // soffit and the ceiling.
    const soffitMaterial = new THREE.MeshStandardMaterial({
        map: ceilingBoardTexture(),
        roughness: 0.93,
        metalness: 0,
        color: 2762273,
        emissive: 6248010,
        emissiveIntensity: 0.16
    });
    const soffitWidth = HALF_W - VAULT_HALF_W;
    for (const side of [-1, 1]) {
        const soffit = new THREE.Mesh(new THREE.PlaneGeometry(soffitWidth, ROOM.d), soffitMaterial);
        soffit.rotation.x = Math.PI / 2;
        soffit.position.set(side * (VAULT_HALF_W + soffitWidth / 2), SOFFIT_Y, 0);
        group.add(soffit);
        const upperWall = new THREE.Mesh(new THREE.PlaneGeometry(ROOM.d, ROOM.h - SOFFIT_Y), new THREE.MeshStandardMaterial({
            color: 15723490,
            roughness: 0.88,
            emissive: 16774378,
            emissiveIntensity: 0.42
        }));
        upperWall.rotation.y = side * -Math.PI / 2;
        upperWall.position.set(side * VAULT_HALF_W, (SOFFIT_Y + ROOM.h) / 2, 0);
        group.add(upperWall);
    }

    {
        // Joists across the soffits.
        const joistGeometries = [];
        const joist = new THREE.BoxGeometry(soffitWidth, 0.12, 0.16);
        for (let z = -ROOM.d / 2 + 0.4; z < ROOM.d / 2; z += 0.8)
            for (const side of [-1, 1]) {
                const copy = joist.clone();
                copy.translate(side * (VAULT_HALF_W + soffitWidth / 2), SOFFIT_Y - 0.06, z);
                joistGeometries.push(copy);
            }
        const joists = new THREE.Mesh(mergeGeometries(joistGeometries, false), new THREE.MeshStandardMaterial({
            roughness: 0.94,
            color: 14605001
        }));
        group.add(joists);
        joist.dispose();
    }

    // Per-alcove interior finishes, keyed by zone id.
    const alcoveMaterials = {
        _default: {
            floor: new THREE.MeshStandardMaterial({
                map: floorColorMap(),
                roughnessMap: floorRoughnessMap(),
                roughness: 0.9,
                metalness: 0,
                color: 0x48341f
            }),
            wall: new THREE.MeshStandardMaterial({
                color: 2501425,
                roughness: 0.97
            }),
            ceil: new THREE.MeshStandardMaterial({
                color: 3091238,
                roughness: 0.95,
                emissive: 6971477,
                emissiveIntensity: 0.16
            })
        },
        "listening-room": {
            floor: new THREE.MeshStandardMaterial({
                map: carpetTexture(),
                roughness: 1,
                metalness: 0,
                color: 4857896
            }),
            wall: new THREE.MeshStandardMaterial({
                color: 5906472,
                roughness: 0.98
            }),
            ceil: new THREE.MeshStandardMaterial({
                color: 15129810,
                roughness: 0.95,
                emissive: 16773346,
                emissiveIntensity: 0.3
            })
        },
        "reggae-room": {
            floor: new THREE.MeshStandardMaterial({
                map: floorColorMap(),
                roughnessMap: floorRoughnessMap(),
                roughness: 0.9,
                metalness: 0,
                color: 9073752
            }),
            wall: new THREE.MeshStandardMaterial({
                color: 2243116,
                roughness: 0.96
            }),
            ceil: new THREE.MeshStandardMaterial({
                color: 15525850,
                roughness: 0.95,
                emissive: 16774378,
                emissiveIntensity: 0.34
            })
        }
    };
    for (const box of ALCOVE_BOXES) {
        const centerX = alcoveCenterX(box);
        const materials = alcoveMaterials[box.id] || alcoveMaterials._default;
        const openingX = box.side * HALF_W;
        const backX = box.side * (HALF_W + box.w);

        const floor = new THREE.Mesh(new THREE.PlaneGeometry(box.w, box.d), materials.floor);
        floor.rotation.x = -Math.PI / 2;
        floor.position.set(centerX, 0.001, box.z);
        floor.receiveShadow = true;
        group.add(floor);

        const ceiling = new THREE.Mesh(new THREE.PlaneGeometry(box.w, box.d), materials.ceil);
        ceiling.rotation.x = Math.PI / 2;
        ceiling.position.set(centerX, box.h, box.z);
        group.add(ceiling);

        const backWall = new THREE.Mesh(new THREE.PlaneGeometry(box.w, box.h), materials.wall);
        backWall.rotation.y = box.side < 0 ? -Math.PI / 2 : Math.PI / 2;
        backWall.position.set(backX, box.h / 2, box.z);
        backWall.receiveShadow = true;
        group.add(backWall);

        for (const end of [-1, 1]) {
            const endWall = new THREE.Mesh(new THREE.PlaneGeometry(box.w, box.h), materials.wall);
            endWall.rotation.y = end > 0 ? Math.PI : 0;
            endWall.position.set(centerX, box.h / 2, box.z + end * box.d / 2);
            endWall.receiveShadow = true;
            group.add(endWall);
        }

        // The returns either side of the opening.
        const returnWidth = (box.d - box.openingW) / 2;
        for (const end of [-1, 1]) {
            const returnWall = new THREE.Mesh(new THREE.PlaneGeometry(returnWidth, box.h), materials.wall);
            returnWall.rotation.y = box.side < 0 ? -Math.PI / 2 : Math.PI / 2;
            returnWall.position.set(openingX, box.h / 2, box.z + end * (box.openingW / 2 + returnWidth / 2));
            group.add(returnWall);
        }

        const jambMaterial = new THREE.MeshStandardMaterial({
            color: 2367257,
            roughness: 1,
            metalness: 0
        });
        const jambGeometries = [];
        for (const end of [-1, 1]) {
            const jamb = new THREE.BoxGeometry(JAMB_D, box.h, 0.1);
            jamb.translate(openingX + box.side * JAMB_D / 2, box.h / 2, box.z + end * (box.openingW / 2 + 0.05));
            jambGeometries.push(jamb);
        }
        const head = new THREE.BoxGeometry(JAMB_D, 0.12, box.openingW + 0.2);
        head.translate(openingX + box.side * JAMB_D / 2, box.h - 0.06, box.z);
        jambGeometries.push(head);
        const jambs = new THREE.Mesh(mergeGeometries(jambGeometries, false), jambMaterial);
        jambs.castShadow = true;
        jambs.receiveShadow = true;
        group.add(jambs);
    }

    // Cornice / trim runs along the springing of the vault and under the soffits.
    const trimMaterial = new THREE.MeshStandardMaterial({
        color: 6971735,
        roughness: 0.85,
        emissive: 6971477,
        emissiveIntensity: 0.16
    });
    const trimGeometries = [];
    const addTrim = (length, x, y, z, yaw, depth, height) => {
        const geometry = new THREE.BoxGeometry(length, height, depth);
        geometry.rotateY(yaw);
        geometry.translate(x, y, z);
        trimGeometries.push(geometry);
    };
    for (const [z0, z1, ceilingY] of CEILING_SECTIONS) {
        const length = z1 - z0;
        const centerZ = (z0 + z1) / 2;
        const springY = ceilingY - VAULT_RISE;
        for (const side of [-1, 1]) {
            addTrim(length, side * (VAULT_HALF_W - 0.055), springY + 0.03, centerZ, Math.PI / 2, 0.19, 0.06);
            addTrim(length, side * (VAULT_HALF_W - 0.045), springY - 0.055, centerZ, Math.PI / 2, 0.15, 0.12);
            addTrim(length, side * (VAULT_HALF_W - 0.03), springY - 0.155, centerZ, Math.PI / 2, 0.1, 0.09);
            if (springY < SOFFIT_Y - 0.02) addTrim(length, side * (VAULT_HALF_W - 0.02), (springY + SOFFIT_Y) / 2 + 0.03, centerZ, Math.PI / 2, 0.05, SOFFIT_Y - springY);
        }
    }
    for (const [z, ceilingY] of [
            [-ROOM.d / 2 + 0.075, CEILING_SECTIONS[2][2]],
            [ROOM.d / 2 - 0.075, CEILING_SECTIONS[0][2]]
        ]) addTrim(VAULT_HALF_W * 2, 0, ceilingY - VAULT_RISE - 0.055, z, 0, 0.15, 0.12);
    for (const side of [-1, 1]) {
        addTrim(ROOM.d, side * (HALF_W - 0.085), SOFFIT_Y - 0.04, 0, Math.PI / 2, 0.17, 0.06);
        addTrim(ROOM.d, side * (HALF_W - 0.05), SOFFIT_Y - 0.135, 0, Math.PI / 2, 0.1, 0.15);
    }
    for (const z of [-ROOM.d / 2 + 0.085, ROOM.d / 2 - 0.085])
        for (const side of [-1, 1]) {
            const x = side * (VAULT_HALF_W + soffitWidth / 2);
            addTrim(soffitWidth, x, SOFFIT_Y - 0.04, z, 0, 0.17, 0.06);
            addTrim(soffitWidth, x, SOFFIT_Y - 0.135, z, 0, 0.1, 0.15);
        }
    const trim = new THREE.Mesh(mergeGeometries(trimGeometries, false), trimMaterial);
    trim.castShadow = false;
    group.add(trim);

    {
        // Conduit run along the right-hand upper wall.
        const pipeGeometry = new THREE.CylinderGeometry(0.018, 0.018, ROOM.d, 6);
        pipeGeometry.rotateX(Math.PI / 2);
        const pipe = new THREE.Mesh(pipeGeometry, new THREE.MeshStandardMaterial({
            color: 1315344,
            roughness: 0.85
        }));
        pipe.position.set(VAULT_HALF_W - 0.022, (SOFFIT_Y + ROOM.h) / 2, 0);
        group.add(pipe);
    }

    const skirtingMaterial = new THREE.MeshStandardMaterial({
        map: woodTexture(-14),
        roughness: 0.88,
        color: 9075299
    });
    const skirting = (length, position, yaw) => {
        const mesh = new THREE.Mesh(new THREE.BoxGeometry(length, 0.14, 0.04), skirtingMaterial);
        mesh.position.set(...position);
        mesh.rotation.y = yaw;
        group.add(mesh);
    };
    skirting(ROOM.w, [0, 0.07, -ROOM.d / 2 + 0.02], 0);
    skirting(ROOM.d, [-HALF_W + 0.02, 0.07, 0], Math.PI / 2);
    skirting(ROOM.d, [HALF_W - 0.02, 0.07, 0], Math.PI / 2);

    {
        // Hanging section signs. One tall canvas, one row per sign, sampled by
        // re-cutting each board's v range.
        const signs = [{
            x: -1.45,
            z: 6.4,
            name: "ROCK & PUNK",
            ink: "#241a12",
            card: "#efe4cf"
        }, {
            x: 1.45,
            z: 2.9,
            name: "HIP-HOP",
            ink: "#1c1c1e",
            card: "#ecebe4"
        }, {
            x: -1.45,
            z: -0.5,
            name: "FUNK & DISCO",
            ink: "#26141f",
            card: "#efdfd2"
        }, {
            x: 1.45,
            z: -3.8,
            name: "ELECTRONIC",
            ink: "#101820",
            card: "#dee6ea"
        }, {
            x: -1.45,
            z: -6.9,
            name: "JAZZ",
            ink: "#1e1509",
            card: "#eadfc4"
        }];
        const signCount = signs.length;
        const tileW = 512;
        const tileH = 128;
        const [canvas, ctx] = makeCanvas(tileW, tileH * signCount);
        for (let i = 0; i < signCount; i++) {
            const sign = signs[i];
            const top = i * tileH;
            ctx.fillStyle = sign.card;
            ctx.fillRect(0, top, tileW, tileH);
            ctx.fillStyle = "rgba(0,0,0,.10)";
            ctx.fillRect(0, top, tileW, 6);
            ctx.fillRect(0, top + tileH - 5, tileW, 5);
            ctx.fillStyle = sign.ink;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            let fontSize = 74;
            do {
                ctx.font = `700 ${fontSize}px Helvetica, Arial, sans-serif`;
                fontSize -= 2;
            } while (fontSize > 24 && ctx.measureText(sign.name).width > tileW - 48);
            ctx.fillText(sign.name, tileW / 2, top + tileH / 2 + 2);
        }
        const signTexture = new THREE.CanvasTexture(canvas);
        signTexture.colorSpace = THREE.SRGBColorSpace;
        signTexture.anisotropy = 4;

        const boardGeometries = [];
        const rodGeometries = [];
        const boardW = 0.66;
        const boardH = 0.165;
        const boardOffset = 0.01;
        const rodMaterial = new THREE.MeshStandardMaterial({
            color: 1709844,
            roughness: 1
        });
        for (let i = 0; i < signCount; i++) {
            const {
                x,
                z
            } = signs[i];
            const vBase = 1 - (i + 1) / signCount;
            for (const yaw of [0, Math.PI]) {
                const board = new THREE.PlaneGeometry(boardW, boardH);
                const uv = board.attributes.uv;
                for (let k = 0; k < uv.count; k++) uv.setY(k, vBase + uv.getY(k) / signCount);
                uv.needsUpdate = true;
                board.rotateY(yaw);
                board.translate(x, SIGN_Y, z + (yaw ? -boardOffset : boardOffset));
                boardGeometries.push(board);
            }
            const rodLength = ceilingHeightAt(z) - (SIGN_Y + boardH / 2);
            for (const side of [-1, 1]) {
                const rod = new THREE.CylinderGeometry(0.0035, 0.0035, rodLength, 4);
                rod.translate(x + side * 0.28, SIGN_Y + boardH / 2 + rodLength / 2, z);
                rodGeometries.push(rod);
            }
        }
        group.add(new THREE.Mesh(mergeGeometries(boardGeometries, false), new THREE.MeshStandardMaterial({
            map: signTexture,
            roughness: 0.9,
            metalness: 0,
            emissiveMap: signTexture,
            emissive: 16771276,
            emissiveIntensity: 0.3
        })));
        group.add(new THREE.Mesh(mergeGeometries(rodGeometries, false), rodMaterial));
    }

    parent.add(group);
    return group;
}

// ---------------------------------------------------------------------------
// buildLights
// ---------------------------------------------------------------------------

// Aisle spot origin lift (m above the bulb) and intensity trim; see spot().
const SPOT_LIFT = 0.15;
const SPOT_TRIM = 0.6;

// Per-genre intensity trim.
const LIGHT_TRIM = {
    rock: 1,
    hiphop: 1,
    funk: 1,
    electronic: 0.97,
    jazz: 0.92,
    soul: 1,
    dub: 1
};

function buildLights(scene) {
    const group = new THREE.Group();
    let lightCount = 0;
    // Lights added through here are counted against LIGHT_BUDGET.
    const addLight = (object, ...rest) => {
        if (object.isLight) lightCount++;
        if (lightCount > LIGHT_BUDGET) throw new Error(`light budget blown: ${lightCount}`);
        group.add(object, ...rest);
        return object;
    };

    scene.fog = new THREE.FogExp2(0x1c1710, 0.02);
    const hemi = new THREE.HemisphereLight(0x676a6c, 0x44301f, 0.78);
    hemi.name = "roomHemi";
    scene.add(hemi);
    lightCount++;

    // Emissive-only materials: they glow, they illuminate nothing.
    const glowMaterial = (r, g, b, fog = false) => {
        const material = new THREE.MeshBasicMaterial({
            fog
        });
        material.color.setRGB(r, g, b);
        return material;
    };
    const panelGlow = glowMaterial(0.86, 0.84, 0.79);
    const battenGlow = glowMaterial(0.42, 0.41, 0.385);
    const conduitGlowWarm = glowMaterial(0.44, 0.33, 0.2);
    const conduitGlowDim = glowMaterial(0.41, 0.37, 0.29);
    const bulbGlow = glowMaterial(1.7, 1.47, 1.11);
    const neonRed = glowMaterial(1.65, 0.11, 0.18);
    const neonCyan = glowMaterial(0.14, 0.92, 1.16);

    const cordMaterial = new THREE.MeshStandardMaterial({
        color: 1709330,
        roughness: 1
    });
    const roseMaterial = new THREE.MeshStandardMaterial({
        color: 1314830,
        roughness: 0.9
    });
    const globeMaterial = new THREE.MeshStandardMaterial({
        color: 2762272,
        emissive: 16773852,
        emissiveIntensity: 2.4,
        emissiveMap: bulbGradientTexture(),
        roughness: 0.42
    });

    const pendantParts = {
        cords: [],
        roses: [],
        globes: [],
        shades: []
    };
    const shadeHeight = 0.3;
    const pendantBulbY = 2.3;

    function pendant(x, z, bulbY, ceilingY) {
        const cord = new THREE.CylinderGeometry(0.008, 0.008, ceilingY - bulbY - 0.06, 5);
        cord.translate(x, bulbY + 0.06 + (ceilingY - bulbY - 0.06) / 2, z);
        pendantParts.cords.push(cord);
        const rose = new THREE.CylinderGeometry(0.075, 0.075, 0.035, 10);
        rose.translate(x, ceilingY - 0.018, z);
        pendantParts.roses.push(rose);
        const shade = new THREE.CylinderGeometry(0.055, shadeHeight, 0.2, 20, 1, true);
        shade.translate(x, bulbY + 0.1, z);
        pendantParts.shades.push(shade);
        const collar = new THREE.CylinderGeometry(0.055, 0.055, 0.02, 12);
        collar.translate(x, bulbY + 0.2, z);
        pendantParts.shades.push(collar);
        const globe = new THREE.SphereGeometry(0.155, 16, 9, 0, Math.PI * 2, Math.PI * 0.46, Math.PI * 0.54);
        globe.translate(x, bulbY + 0.055, z);
        pendantParts.globes.push(globe);
    }
    for (const z of PENDANT_Z) pendant(PENDANT_X, z, pendantBulbY, ceilingHeightAt(z));

    {
        // One merged additive mesh of open cones: a faint light shaft under each pendant.
        const [canvas, ctx] = makeCanvas(8, 64);
        const gradient = ctx.createLinearGradient(0, 0, 0, 64);
        gradient.addColorStop(0, "rgba(255,255,255,1)");
        gradient.addColorStop(0.5, "rgba(255,255,255,0.35)");
        gradient.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, 8, 64);
        const texture = new THREE.CanvasTexture(canvas);
        texture.colorSpace = THREE.SRGBColorSpace;
        const shaftTop = pendantBulbY + 0.05;
        const shaftHeight = shaftTop - 0.05;
        const cones = PENDANT_Z.map(z => {
            const cone = new THREE.CylinderGeometry(0.1, 0.4, shaftHeight, 12, 1, true);
            cone.translate(PENDANT_X, shaftTop - shaftHeight / 2, z);
            return cone;
        });
        const shafts = new THREE.Mesh(mergeGeometries(cones, false), new THREE.MeshBasicMaterial({
            map: texture,
            color: new THREE.Color().setRGB(1, 0.8, 0.52),
            blending: THREE.AdditiveBlending,
            transparent: true,
            opacity: 0.045,
            depthWrite: false,
            fog: true,
            side: THREE.FrontSide
        }));
        shafts.name = "pendantShafts";
        shafts.renderOrder = 5;
        group.add(shafts);
        for (const cone of cones) cone.dispose();
    }

    // Intensity, throw and cone widen with the room.
    function spot({
        z,
        x = PENDANT_X,
        color,
        intensity,
        distance,
        angle,
        penumbra = 0.62,
        decay = 1.35,
        shadow = false,
        aimX = null
    }) {
        const light = new THREE.SpotLight(color, intensity * SPOT_TRIM * (1 + (WIDE_SCALE - 1) * 0.85), distance * WIDE_SCALE, Math.min(1.45, angle * (1 + (WIDE_SCALE - 1) * 0.55)), penumbra, decay);
        // Irradiance goes as 1 / distance^decay, so a surface a metre under the
        // lamp (a record held at arm's length beneath a pendant) is lit several
        // times harder than the floor. Origin sits SPOT_LIFT above the bulb to
        // flatten that near field, but never closer than 0.3 to the ceiling
        // (the same singularity, upward) and never below the bulb.
        const bulbY = pendantBulbY - 0.03;
        light.position.set(x, Math.max(bulbY, Math.min(bulbY + SPOT_LIFT, ceilingHeightAt(z) - 0.3)), z);
        light.target.position.set(aimX === null ? x : aimX, 0, z);
        if (shadow) {
            light.castShadow = true;
            light.shadow.mapSize.set(1024, 1024);
            light.shadow.bias = -0.004;
            light.shadow.camera.far = 6;
        }
        return addLight(light, light.target);
    }

    {
        const trim = LIGHT_TRIM.rock;
        spot({
            z: 6.4,
            x: 0,
            color: 16773340,
            intensity: 17 * trim,
            distance: 8.6,
            angle: 1.15,
            penumbra: 0.62,
            decay: 1.3,
            shadow: true
        });
        const neonGeometries = [];
        {
            const tube = new THREE.CylinderGeometry(0.017, 0.017, 2.3, 6);
            tube.rotateX(Math.PI / 2);
            for (const dy of [0.2, -0.2]) {
                const copy = tube.clone();
                copy.translate(-2.855, 2.14 + dy, 6.6);
                neonGeometries.push(copy);
            }
            const stroke = new THREE.CylinderGeometry(0.015, 0.015, 0.6, 6);
            stroke.rotateZ(Math.PI / 2);
            for (const [dy, yaw] of [
                    [0.1, 0],
                    [0, 0.9],
                    [-0.1, 0]
                ]) {
                const copy = stroke.clone();
                copy.rotateY(Math.PI / 2 + yaw);
                copy.translate(-2.855, 2.14 + dy, 6.6);
                neonGeometries.push(copy);
            }
            tube.dispose();
            stroke.dispose();
        }
        group.add(new THREE.Mesh(mergeGeometries(neonGeometries, false), neonRed));
        {
            const backer = new THREE.Mesh(new THREE.PlaneGeometry(2.5, 0.62), new THREE.MeshStandardMaterial({
                color: 1314829,
                roughness: 0.95
            }));
            backer.rotation.y = Math.PI / 2;
            backer.position.set(-HALF_W + 0.015, 2.14, 6.6);
            group.add(backer);
        } {
            const [canvas, ctx] = makeCanvas(24, 96);
            ctx.fillStyle = "#000";
            ctx.fillRect(0, 0, 24, 96);
            ctx.globalCompositeOperation = "lighter";
            for (const [y, alpha] of [
                    [22, 0.85],
                    [58, 0.7]
                ]) glowBlob(ctx, 12, y, 34, alpha);
            const texture = new THREE.CanvasTexture(canvas);
            texture.colorSpace = THREE.SRGBColorSpace;
            const wash = new THREE.Mesh(new THREE.PlaneGeometry(3.4, 1.5), new THREE.MeshBasicMaterial({
                map: texture,
                blending: THREE.AdditiveBlending,
                transparent: true,
                depthWrite: false,
                fog: true,
                color: new THREE.Color().setRGB(0.28, 0.2, 0.11)
            }));
            wash.position.set(-HALF_W + 0.02, 1.85, 6.4);
            wash.rotation.y = Math.PI / 2;
            group.add(wash);
        }
    } {
        const trim = LIGHT_TRIM.hiphop;
        spot({
            z: 2.9,
            x: 0,
            color: 16773340,
            intensity: 17 * trim,
            distance: 7.4,
            angle: 1.15,
            penumbra: 0.62,
            decay: 1.3
        });
        {
            const stripGeometries = [];
            const strip = new THREE.BoxGeometry(0.1, 0.045, 1.5);
            for (const [x, z] of [
                    [-0.6, 2.6],
                    [1.3, 1.9],
                    [-0.6, 4.1]
                ]) {
                const copy = strip.clone();
                copy.translate(x, ceilingHeightAt(z) - 0.03, z);
                stripGeometries.push(copy);
            }
            group.add(new THREE.Mesh(mergeGeometries(stripGeometries, false), conduitGlowDim));
            strip.dispose();
        }
    } {
        const trim = LIGHT_TRIM.funk;
        spot({
            z: -0.9,
            x: 0,
            color: 16773340,
            intensity: 17 * trim,
            distance: 8.6,
            angle: 1.15,
            penumbra: 0.62,
            decay: 1.3,
            shadow: true
        });
        const spinner = new THREE.Group();
        spinner.position.set(0.2, 2.42, -0.5);
        group.add(spinner);
        const ball = new THREE.Mesh(new THREE.IcosahedronGeometry(0.15, 1), new THREE.MeshStandardMaterial({
            color: 14211296,
            roughness: 0.16,
            metalness: 1,
            emissive: 16773336,
            emissiveIntensity: 0.5,
            flatShading: true
        }));
        ball.position.set(0.2, 2.42, -0.5);
        group.add(ball);
        {
            const ceilingY = ceilingHeightAt(-0.5);
            const ballTop = 2.42 + 0.15;
            const cord = new THREE.CylinderGeometry(0.004, 0.004, ceilingY - ballTop, 4);
            cord.translate(0.2, (ballTop + ceilingY) / 2, -0.5);
            pendantParts.cords.push(cord);
        }
        let lastTime = performance.now();
        const spin = now => {
            spinner.rotation.y += 0.55 * Math.min((now - lastTime) / 1e3, 0.1);
            lastTime = now;
            requestAnimationFrame(spin);
        };
        requestAnimationFrame(spin);
    } {
        const trim = LIGHT_TRIM.electronic;
        spot({
            z: -4.4,
            x: 0,
            color: 16773340,
            intensity: 17 * trim,
            distance: 7.4,
            angle: 1.15,
            penumbra: 0.62,
            decay: 1.3
        });
        const zone = ALCOVES.find(candidate => candidate.genres?.includes("electronic"));
        const z = zone ? zone.alcove.z : -3.9;
        const side = zone ? zone.alcove.side : 1;
        const strip = new THREE.BoxGeometry(0.02, 0.1, 0.72);
        strip.translate(side * (HALF_W - 0.03), 2.05, z);
        group.add(new THREE.Mesh(strip, neonCyan));
    } {
        const trim = LIGHT_TRIM.jazz;
        spot({
            z: -7.4,
            x: 0,
            color: 16773340,
            intensity: 17 * trim,
            distance: 8.6,
            angle: 1.15,
            penumbra: 0.62,
            decay: 1.3
        });
        group.add(new THREE.Mesh(new THREE.SphereGeometry(0.055, 8, 6), glowMaterial(2.1, 1.4, 0.72)).translateX(-2.3).translateY(1.45).translateZ(-7.6));
        {
            const stem = new THREE.CylinderGeometry(0.011, 0.014, 1.4, 6);
            stem.translate(-2.3, 0.7, -7.6);
            pendantParts.cords.push(stem);
            const base = new THREE.CylinderGeometry(0.13, 0.15, 0.03, 12);
            base.translate(-2.3, 0.015, -7.6);
            pendantParts.cords.push(base);
        }
    } {
        // Recessed ceiling panels.
        const diffuser = new THREE.PlaneGeometry(1.24, 0.32);
        diffuser.rotateX(Math.PI / 2);
        const housing = new THREE.BoxGeometry(1.4, 0.075, 0.46);
        const dimDiffusers = [];
        const brightDiffusers = [];
        const housings = [];
        for (const z of PANEL_Z) {
            const ceilingY = ceilingHeightAt(z);
            const panel = diffuser.clone();
            panel.translate(0.55, ceilingY - 0.175, z);
            (z === 2.9 ? brightDiffusers : dimDiffusers).push(panel);
            const box = housing.clone();
            box.translate(0.55, ceilingY - 0.14, z);
            housings.push(box);
        }
        group.add(new THREE.Mesh(mergeGeometries(brightDiffusers, false), new THREE.MeshBasicMaterial({
            color: new THREE.Color().setRGB(0.94, 0.9, 0.81),
            fog: false,
            side: THREE.DoubleSide
        })));
        group.add(new THREE.Mesh(mergeGeometries(housings, false), new THREE.MeshStandardMaterial({
            color: 15722972,
            roughness: 0.5,
            side: THREE.DoubleSide,
            emissive: 16768432,
            emissiveIntensity: 0.16
        })));
        group.add(new THREE.Mesh(mergeGeometries(dimDiffusers, false), new THREE.MeshBasicMaterial({
            color: panelGlow.color,
            fog: false,
            side: THREE.DoubleSide
        })));
        diffuser.dispose();
        housing.dispose();
    } {
        // Surface battens at x = 1.42 * WIDE_SCALE and x = -2.05 * WIDE_SCALE,
        // hung just under ceilingHeightAt(z).
        const bodyGeometries = [];
        const body = new THREE.BoxGeometry(0.105, 0.075, 1.24);
        const endCap = new THREE.BoxGeometry(0.105, 0.075, 0.045);
        const tubeGeometries = [];
        const tube = new THREE.BoxGeometry(0.055, 0.022, 1.15);
        for (const z of BATTEN_Z)
            for (const x of [1.42 * WIDE_SCALE, -2.05 * WIDE_SCALE]) {
                const ceilingY = ceilingHeightAt(z);
                const tubeCopy = tube.clone();
                tubeCopy.translate(x, ceilingY - 0.055, z);
                tubeGeometries.push(tubeCopy);
                const bodyCopy = body.clone();
                bodyCopy.translate(x, ceilingY - 0.028, z);
                bodyGeometries.push(bodyCopy);
                for (const dz of [-0.64, 0.64]) {
                    const cap = endCap.clone();
                    cap.translate(x, ceilingY - 0.042, z + dz);
                    bodyGeometries.push(cap);
                }
            }
        group.add(new THREE.Mesh(mergeGeometries(tubeGeometries, false), battenGlow));
        group.add(new THREE.Mesh(mergeGeometries(bodyGeometries, false), new THREE.MeshStandardMaterial({
            color: 14209734,
            roughness: 0.45,
            metalness: 0.25
        })));
        tube.dispose();
        body.dispose();
        endCap.dispose();
    } {
        // Emissive tubes along z at x = -2.48 and x = 2.48, y = 1.92.
        const warmGeometries = [];
        const stub = new THREE.CylinderGeometry(0.012, 0.012, 1.6, 6);
        stub.rotateX(Math.PI / 2);
        const dimGeometries = [];
        for (const x of [-2.48, 2.48]) {
            for (const z of [-4.6, -1.4, 5.2, 7]) {
                const copy = stub.clone();
                copy.translate(x, 1.92, z);
                warmGeometries.push(copy);
            }
            for (const z of [2, 3.8]) {
                const copy = stub.clone();
                copy.translate(x, 1.92, z);
                dimGeometries.push(copy);
            }
        }
        group.add(new THREE.Mesh(mergeGeometries(warmGeometries, false), conduitGlowWarm));
        group.add(new THREE.Mesh(mergeGeometries(dimGeometries, false), conduitGlowDim));
        stub.dispose();
        {
            // Vertical falloff strips washing both side walls.
            const [canvas, ctx] = makeCanvas(8, 64);
            const gradient = ctx.createLinearGradient(0, 0, 0, 64);
            gradient.addColorStop(0, "rgba(255,255,255,0)");
            gradient.addColorStop(0.42, "rgba(255,255,255,0.95)");
            gradient.addColorStop(0.62, "rgba(255,255,255,0.30)");
            gradient.addColorStop(1, "rgba(255,255,255,0)");
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, 8, 64);
            const texture = new THREE.CanvasTexture(canvas);
            texture.colorSpace = THREE.SRGBColorSpace;
            for (const side of [-1, 1]) {
                const wash = new THREE.Mesh(new THREE.PlaneGeometry(ROOM.d - 1.2, 0.55), new THREE.MeshBasicMaterial({
                    map: texture,
                    blending: THREE.AdditiveBlending,
                    transparent: true,
                    depthWrite: false,
                    fog: true,
                    side: THREE.FrontSide,
                    color: new THREE.Color().setRGB(0.08, 0.055, 0.03)
                }));
                wash.position.set(side * (ROOM.w / 2 - 0.05), 1.78, -0.3);
                wash.rotation.y = -side * Math.PI / 2;
                group.add(wash);
            }
        }
    }

    // One point light per alcove, plus its visible fitting.
    for (const box of ALCOVE_BOXES) {
        if (box.entrance) {
            const light = new THREE.PointLight(15920862, 5.6, 6, 1.5);
            light.position.set(alcoveCenterX(box), box.h - 0.55, box.z);
            addLight(light);
            continue;
        }
        const zone = ALCOVES.find(candidate => candidate.id === box.id);
        const color = zone?.light?.main?.color ?? 16768180;
        const trim = LIGHT_TRIM[box.id] ?? 1;
        const x = alcoveCenterX(box);
        const z = box.z;
        const light = new THREE.PointLight(color, 7.4 * trim, 6.2, 1.35);
        light.position.set(x, box.h - 0.65, z);
        addLight(light);
        const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.032, 10, 8), bulbGlow);
        bulb.position.set(x, box.h - 0.52, z);
        group.add(bulb);
        const flex = new THREE.Mesh(new THREE.CylinderGeometry(0.005, 0.005, 0.42, 4), cordMaterial);
        flex.position.set(x, box.h - 0.29, z);
        group.add(flex);
        if (box.id === "listening-room") {
            const shade = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.26, 0.2, 14, 1, true), new THREE.MeshStandardMaterial({
                color: 13208138,
                roughness: 0.95,
                side: THREE.DoubleSide,
                emissive: 16758903,
                emissiveIntensity: 1.15
            }));
            shade.position.set(x, box.h - 0.46, z);
            group.add(shade);
        }
    }

    group.add(new THREE.Mesh(mergeGeometries(pendantParts.cords, false), cordMaterial));
    {
        const shades = new THREE.Mesh(mergeGeometries(pendantParts.shades, false), new THREE.MeshStandardMaterial({
            color: 15920868,
            roughness: 0.34,
            metalness: 0.05,
            side: THREE.DoubleSide,
            emissive: 16768432,
            emissiveIntensity: 0.14
        }));
        shades.castShadow = false;
        group.add(shades);
    }
    group.add(new THREE.Mesh(mergeGeometries(pendantParts.roses, false), roseMaterial));
    {
        const globes = new THREE.Mesh(mergeGeometries(pendantParts.globes, false), globeMaterial);
        globes.castShadow = false;
        group.add(globes);
    } {
        const light = new THREE.PointLight(16767408, 6.4, 9, 1.5);
        light.position.set(1.75, 2.05, 2.2);
        addLight(light);
    } {
        // Three-lobe wash on the right-hand wall.
        const [canvas, ctx] = makeCanvas(128, 32);
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, 128, 32);
        ctx.globalCompositeOperation = "lighter";
        for (const [x, alpha] of [
                [22, 0.8],
                [64, 0.6],
                [106, 0.72]
            ]) glowBlob(ctx, x, 9, 30, alpha);
        const texture = new THREE.CanvasTexture(canvas);
        texture.colorSpace = THREE.SRGBColorSpace;
        const wash = new THREE.Mesh(new THREE.PlaneGeometry(4.6, 1.35), new THREE.MeshBasicMaterial({
            map: texture,
            blending: THREE.AdditiveBlending,
            transparent: true,
            depthWrite: false,
            fog: true,
            color: new THREE.Color().setRGB(0.22, 0.17, 0.1)
        }));
        wash.position.set(HALF_W - 0.02, 1.72, 5.9);
        wash.rotation.y = -Math.PI / 2;
        group.add(wash);
    } {
        // Counter lamp: cone shade, bulb, drop rod, spot and floor pool.
        const x = COUNTER_POS.x;
        const z = COUNTER_POS.z;
        const counterTopY = 1.14;
        const shadeY = 2.62;
        const shade = new THREE.Mesh(new THREE.ConeGeometry(0.2, 0.2, 18, 1, true), new THREE.MeshStandardMaterial({
            color: 2366743,
            roughness: 0.85,
            side: THREE.DoubleSide,
            emissive: 16768168,
            emissiveIntensity: 0.55
        }));
        shade.position.set(x, shadeY + 0.1, z);
        group.add(shade);
        const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.035, 10, 8), bulbGlow);
        bulb.position.set(x, shadeY + 0.045, z);
        group.add(bulb);
        {
            const rod = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.006, SOFFIT_Y - shadeY - 0.19, 5), cordMaterial);
            rod.position.set(x, (SOFFIT_Y + shadeY + 0.19) / 2, z);
            group.add(rod);
        }
        const light = new THREE.SpotLight(16768942, 13, 5.4, 0.78, 0.52, 1.45);
        light.position.set(x, shadeY + 0.02, z);
        light.target.position.set(x - 0.1, counterTopY - 0.1, z);
        addLight(light, light.target);
        {
            const [canvas, ctx] = makeCanvas(64, 64);
            ctx.fillStyle = "#000";
            ctx.fillRect(0, 0, 64, 64);
            ctx.globalCompositeOperation = "lighter";
            glowBlob(ctx, 32, 32, 30, 0.95);
            const texture = new THREE.CanvasTexture(canvas);
            texture.colorSpace = THREE.SRGBColorSpace;
            const pool = new THREE.Mesh(new THREE.PlaneGeometry(1.5, 1.5), new THREE.MeshBasicMaterial({
                map: texture,
                blending: THREE.AdditiveBlending,
                transparent: true,
                depthWrite: false,
                fog: true,
                color: new THREE.Color().setRGB(0.3, 0.23, 0.14)
            }));
            pool.rotation.x = -Math.PI / 2;
            pool.position.set(x - 0.08, counterTopY + 0.045, z);
            group.add(pool);
        }
    }

    scene.add(group);
    return group;
}

// Soft contact shadow under every low static mesh: the XZ footprint of each
// triangle near the floor is rasterised into a canvas, blurred, and used as
// the alpha of one black plane just above the floor. The blur keeps the
// darkening outside the footprint, so the furniture itself is not tinted.
function bakeFloorAO(scene, { pxPerMetre = 100, maxY = 0.6, opacity = 0.7 } = {}) {
    const width = Math.round(ROOM.w * pxPerMetre);
    const height = Math.round(ROOM.d * pxPerMetre);
    const [canvas, ctx] = makeCanvas(width, height);
    const toX = x => (x + ROOM.w / 2) * pxPerMetre;
    const toY = z => (ROOM.d / 2 - z) * pxPerMetre;
    const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
    ctx.fillStyle = "#fff";
    scene.updateMatrixWorld(true);
    scene.traverse(mesh => {
        if (!mesh.isMesh || mesh.isInstancedMesh || mesh.isSkinnedMesh || !mesh.visible) return;
        if (mesh.material && (mesh.material.transparent || mesh.material.isMeshBasicMaterial)) return;
        const geometry = mesh.geometry;
        const position = geometry && geometry.attributes.position;
        if (!position) return;
        geometry.computeBoundingBox();
        const box = geometry.boundingBox.clone().applyMatrix4(mesh.matrixWorld);
        if (box.min.y > maxY || box.max.y < 0.02) return;
        // Skip floor-sized surfaces (floor, ceiling, runner, rug, wall panels).
        if (box.max.x - box.min.x > ROOM.w * 0.6 || box.max.z - box.min.z > ROOM.d * 0.6) return;
        const index = geometry.index;
        const count = index ? index.count : position.count;
        for (let i = 0; i < count; i += 3) {
            a.fromBufferAttribute(position, index ? index.getX(i) : i).applyMatrix4(mesh.matrixWorld);
            b.fromBufferAttribute(position, index ? index.getX(i + 1) : i + 1).applyMatrix4(mesh.matrixWorld);
            c.fromBufferAttribute(position, index ? index.getX(i + 2) : i + 2).applyMatrix4(mesh.matrixWorld);
            if (Math.min(a.y, b.y, c.y) > maxY || Math.max(a.y, b.y, c.y) < 0.02) continue;
            ctx.beginPath();
            ctx.moveTo(toX(a.x), toY(a.z));
            ctx.lineTo(toX(b.x), toY(b.z));
            ctx.lineTo(toX(c.x), toY(c.z));
            ctx.closePath();
            ctx.fill();
        }
    });
    // Blurred greyscale copy (alphaMap reads the green channel), with the sharp
    // footprint painted black on top so the shadow sits outside it.
    const [blurred, blurCtx] = makeCanvas(width, height);
    blurCtx.fillStyle = "#000";
    blurCtx.fillRect(0, 0, width, height);
    blurCtx.filter = `blur(${Math.round(pxPerMetre * 0.09)}px)`;
    blurCtx.drawImage(canvas, 0, 0);
    blurCtx.filter = "brightness(0)";
    blurCtx.drawImage(canvas, 0, 0);
    blurCtx.filter = "none";
    const texture = new THREE.CanvasTexture(blurred);
    const plane = new THREE.Mesh(new THREE.PlaneGeometry(ROOM.w, ROOM.d), new THREE.MeshBasicMaterial({
        color: 0x000000,
        alphaMap: texture,
        transparent: true,
        opacity,
        depthWrite: false,
        fog: true,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2
    }));
    plane.rotation.x = -Math.PI / 2;
    plane.position.set(0, 0.006, 0);
    plane.renderOrder = 3;
    plane.name = "floorAO";
    scene.add(plane);
    return plane;
}

export { ROOM, LIGHT_BUDGET, buildRoom, buildLights, bakeFloorAO };
